<template>
  <div class="editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <router-link
          :to="{ name: 'pdf_report_editor', params: { report_id: reportId } }">
          <b-button
            type="is-light"
            class="back-btn mr-5"
            icon-right="arrow-left" />
        </router-link>
        <div class="report-breadcrumbs">
          <div>
            <h4>Report</h4>
            <h2>{{ reportData?.name || "Pdf Report" }}</h2>
          </div>

          <div class="arrow-right">
            <i class="material-icons">chevron_right </i>
          </div>

          <div>
            <h4>Module</h4>
            <h2>{{ module?.title || moduleConfigs.name }}</h2>
          </div>
        </div>
      </div>

      <div class="controls">
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          hotkeys="ctrl+shift+d"
          class="button is-light"
          @click="getData">
          Discard Changes
        </a-button>
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          :loading="isSaving"
          hotkeys="ctrl+shift+s"
          class="button is-primary"
          @click="save">
          Publish Changes
        </a-button>
      </div>
    </div>
    <div class="editor-body">
      <div class="block layout">
        <div class="block-header">
          <h4 class="">
            Tracker Table
          </h4>
        </div>

        <div
          v-if="module"
          class="block-body">
          <b-tooltip
            style="width: 100%"
            position="is-right"
            type="is-light"
            :append-to-body="true"
            multilined>
            <div
              class="row mini-view-item"
              :class="{
                selected: selectedRowIndex === null,
                'error-border': tableSettingsErrors.length > 0,
              }"
              @click="openTrackerRowModule(null)">
              <img
                :src="moduleConfigs.icon"
                alt="" />

              <div>
                {{ module?.title || moduleConfigs.name }}
              </div>
            </div>
            <template #content>
              <div class="data-tooltip">
                <div>
                  <h2>Time Dimension:</h2>
                  {{
                    module?.time_dimension?.name ||
                      module?.time_dimension?.key ||
                      "Null"
                  }}
                </div>
                <div v-if="module?.tracker_pivot">
                  <h2>Pivot:</h2>
                  {{
                    module?.tracker_pivot?.dimension?.name ||
                      module?.tracker_pivot?.dimension?.key ||
                      "Null"
                  }}
                </div>
              </div>
            </template>
          </b-tooltip>

          <MiniTrackerTable
            v-if="!isLoading && module"
            :key="module.key"
            v-model="module"
            :row-errors="trackerRowsErrors"
            :current-selected-index="selectedRowIndex"
            @open="openTrackerRowModule" />
        </div>
      </div>
      <div class="editor-and-preview">
        <splitpanes class="default-theme">
          <pane
            min-size="20"
            class="pane">
            <div class="block editor">
              <TrackerRow
                v-if="selectedRowIndex !== null"
                v-model="module.tracker_rows[selectedRowIndex]"
                :errors="selectedRowErrors"
                :context="{
                  timeDimension: module?.time_dimension,
                  pivotDimension: module?.tracker_pivot?.dimension,
                  tableDefaultTimeRange,
                  timeRangeDefaultLabel: 'Use table default',
                  timeRangeDefaultName: trackerTimeRangeDefaultName,
                  trackerTotalHeaders: module?.tracker_total_headers || [],
                }" />
              <TrackerTableEditor
                v-else
                :key="module?.key"
                v-model="module"
                :errors="tableSettingsErrors"
                :report-default-time-range="reportData?.default_time_range" />
            </div>
          </pane>
          <pane min-size="20">
            <PreviewAndJSONEditor
              v-model="module"
              type="module"
              :default-time-range="reportData?.default_time_range"
              @validation-errors="moduleErrors = $event" />
          </pane>
        </splitpanes>
      </div>
    </div>
    <b-loading
      v-model="isLoading"
      :is-full-page="true"
      :can-cancel="false" />

    <b-modal
      v-model="showUnsavedModal"
      :destroy-on-hide="true"
      trap-focus
      close-button-aria-label="Close"
      aria-modal
      custom-class="unsaved-modal-simple">
      <div class="unsaved-modal-content">
        <div class="unsaved-modal-title-row">
          <span class="unsaved-modal-title">You have unsaved changes</span>
        </div>
        <div class="unsaved-modal-body">
          <p>Do you want to save your changes before leaving this page?</p>
        </div>
        <div class="unsaved-modal-actions custom-unsaved-modal-actions">
          <b-button
            type="is-outlined"
            size="is-small"
            class="cancel-btn"
            @click="closeUnsavedModal">
            Cancel
          </b-button>
          <div class="right-actions">
            <b-button
              type="is-muted"
              size="is-small"
              class="discard-btn"
              @click="discardAndNavigate">
              Discard
            </b-button>
            <b-button
              size="is-small"
              class="save-btn"
              @click="saveAndNavigate">
              Save Changes
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import MiniTrackerTable from '@/components/pdf_report/MiniTrackerTableModule.vue'
  import TrackerTableEditor from '@/components/pdf_report/tracker_table_module/TrackerTableEditor.vue'
  import TrackerRow from '@/components/pdf_report/tracker_table_module/TrackerRow.vue'
  import uniqid from 'uniqid'
  import { Splitpanes, Pane } from 'splitpanes'
  import PreviewAndJSONEditor from '@/components/pdf_report/PreviewAndJsonEditor.vue'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  export default {
    name: 'TrackerTableEditorPage',
    components: {
      MiniTrackerTable,
      TrackerTableEditor,
      TrackerRow,
      Splitpanes,
      Pane,
      PreviewAndJSONEditor,
    },
    beforeRouteLeave (to, from, next) {
      if (this.isDirty) {
        this.showUnsavedModal = true
        this.nextRoute = { to, from, }
        this.nextRouteCallback = next
      } else {
        next()
      }
    },
    props: {},
    data () {
      return {
        defaultTimeRange: {
          include_today: false,
          key: '',
          name: '',
          type: '',
        },
        moduleId: null,
        reportId: null,
        module: null,
        reportData: null,

        moduleConfigs: {
          name: 'Tracker Table',
          type: 'TrackerTableModule',
          icon: require('@/assets/icons/tracker-table.svg'),
        },

        selectedRowIndex: null,
        moduleErrors: [],

        isLoading: false,
        isDirty: false,
        isSaving: false,
        showUnsavedModal: false,
        nextRoute: null,
        nextRouteCallback: null,
      }
    },

    computed: {
      tableDefaultTimeRange () {
        return this.module?.default_time_range || this.reportData?.default_time_range || null
      },
      trackerTimeRangeDefaultName () {
        return this.tableDefaultTimeRange?.name || ''
      },
      tableSettingsErrors () {
        return this.moduleErrors.filter(e => {
          const root = e.loc?.[0]
          return root !== 'tracker_rows'
        })
      },
      trackerRowsErrors () {
        return this.childErrors('tracker_rows', this.moduleErrors)
      },
      selectedRowErrors () {
        if (this.selectedRowIndex === null) return []
        return this.childErrors(this.selectedRowIndex, this.trackerRowsErrors)
      },
    },
    watch: {
      module: {
        handler () {
          this.isDirty = true
        },
        deep: true,
      },
    },

    async mounted () {
      this.moduleId = this.$route.params.module_id
      this.reportId = this.$route.params.report_id

      await Promise.all([
        this.getData(),
        this.loadCompanyConfigs(),
      ])

      if (!this.module.key) {
        this.module.key = uniqid()
      }

      this.isLoading = false
    },
    methods: {
      childErrors: _childErrors,
      async loadCompanyConfigs () {
        await this.$store.dispatch('app/reportsConfig/refreshOptions')
      },

      async getData () {
        this.isLoading = true
        this.moduleErrors = []
        const response = await this.$apis.pdfReports.getPdfModuleData(
          this.moduleId
        )
        this.module = response

        this.reportData = await this.$apis.pdfReports.getPdfReportData(
          this.reportId
        )

        await this.$nextTick()
        this.isDirty = false
        this.isLoading = false
      },

      removeReport (key) {
        this.$emit('remove', key)
      },

      onError (error) {
        console.log('error', error)
      },

      showModulePicker () {},

      makeInputsDirty () {},

      closeUnsavedModal () {
        this.showUnsavedModal = false
        this.nextRoute = null
        this.nextRouteCallback = null
      },

      discardAndNavigate () {
        this.showUnsavedModal = false
        if (this.nextRouteCallback) {
          this.nextRouteCallback()
        }
      },

      async saveAndNavigate () {
        await this.save()
        if (this.nextRouteCallback) {
          this.nextRouteCallback()
        }
      },

      async save () {
        this.isSaving = true
        this.isDirty = false
        const response = await this.$apis.pdfReports.updatePdfReportModule(
          this.module
        )
        this.isSaving = false

        if (!response) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Backend Error, Report this to the devs',
            type: 'is-danger',
          })
          return
        }

        if (response?.errors) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Error saving module, Fix the errors and try again',
            type: 'is-warning',
          })
          this.moduleErrors = response.errors
        } else {
          this.module = response
          this.moduleErrors = []
          this.$buefy.toast.open({
            message: 'Changes Saved',
            type: 'is-success',
            position: 'is-bottom',
          })

          await this.$nextTick()
          await this.$nextTick()
          this.isDirty = false
        }
      },

      async deleteModule () {
        this.isLoading = true

        if (await this.$apis.pdfReports.deletePdfReportModule(this.moduleId)) {
          this.$router.push({
            name: 'pdf_report_editor',
            params: { report_id: this.reportId, },
          })
        } else {
          this.$buefy.toast.open({
            message: 'Error deleting module',
            type: 'is-danger',
            position: 'is-bottom',
          })
        }
        this.isLoading = true
      },

      openTrackerRowModule (index = null) {
        this.selectedRowIndex = index
      },
    },
  }
</script>

<style lang="scss" scoped>
.data-tooltip {
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;

  hr {
    margin: 5px 0;
    border: 0;
    border-top: 1px solid black;
  }
  h2 {
    font-size: 12px;
    font-weight: 600;
  }
}
.report-breadcrumbs {
  display: flex;

  h2 {
    font-weight: 400;
    font-size: 20px;
    margin: 0;
  }

  h4 {
    font-weight: 400;
    font-size: 12px;
    margin: 0;
    color: $grayscale-3;
  }

  .arrow-right {
    display: flex;
    align-items: center;
    margin: 0 10px;
    font-size: 20px;
    color: #b0b0b0;
  }
}
.back-btn {
  font-size: 20px !important;
  border-radius: 100% !important;
}
.editor-container {
  padding: 0 20px 5px;
  width: 100%;
  height: 100%;
  margin: auto !important;
  background-color: white;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden !important;
}

.editor-body {
  display: flex;
  max-height: 100%;
  margin: 0 !important;
  overflow: hidden !important;
}

.block {
  border: $border-1;
  border-radius: 3px;
  height: 100%;
  flex-direction: column;
  display: flex;

  .block-header {
    min-height: 40px;
    align-items: center;
    padding: 0 5px;
    border-bottom: $border-1;
    background-color: white;
    display: flex;

    h4 {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      padding: 0 10px;
      text-transform: capitalize;
      margin: 0;
      color: black;
    }
  }
  .block-body {
    padding: 10px 10px;
    background-color: #fbfbfb;
    height: 100%;
    width: 100%;
    flex-direction: column;
    display: flex;
  }

  &.layout {
    max-height: 100%;
    overflow: hidden !important;
    margin-right: 10px;
    min-width: 300px;
    margin-bottom: 0 !important;

    .block-body {
      overflow-y: auto;
    }
  }

  &.editor {
    margin: 0 !important;
  }

  &.preview {
    height: 100%;
    .block-header {
      width: 100%;
    }

    .block-body {
      width: 100%;
      height: 100%;
      display: flex;
      overflow: hidden;
    }

    .output {
      background-color: white;
      height: 100%;
      overflow: auto;
    }
  }
}

.editor-and-preview {
  width: 100%;
}

.editor-header {
  margin-bottom: 20px;

  display: flex;
  padding-top: 10px;
  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 15px;
    align-items: center;
    img {
      width: 35px;
      height: 35px;
      margin-right: 10px;
      border: $border-1;
      border-radius: 8px;
      padding: 5px;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .controls {
    margin-left: auto;
    display: flex;
    align-items: center;

    .button {
      padding: 0 15px;
      font-size: 12px;
    }
  }
}

.mini-view-item {
  cursor: pointer;
  transition: background-color 0.3s ease-in-out, color 0.3s;
  width: 100%;
  padding: 10px;
  display: flex;
  border-radius: 4px;
  align-items: center;
  font-size: 15px;
  background-color: white;
  border: 1px solid #f5f5f5;
  box-shadow: 0px 1px 4px 0px rgba(40, 39, 43, 0.02),
    0px 1px 2px 0px rgba(40, 39, 43, 0.06);
  &.add-button {
    background-color: rgba(0, 0, 0, 0.03);
    transition: background-color 0.1s;
    border: $border-1;
    display: flex;
    align-items: center;
  }
  img {
    width: 15px;
    height: 15px;
    margin-right: 10px;
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    transition: background-color 0.1s;
  }
  &.selected {
    background-color: $primary;
    color: white;
    img {
      filter: brightness(0) invert(1);
    }
  }
}

.unsaved-modal-simple {
  .modal-content {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
}
.unsaved-modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px 24px 16px 24px;
  min-width: 320px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.unsaved-modal-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.unsaved-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
}
.unsaved-modal-close {
  background: none;
  border: none;
  font-size: 22px;
  color: #888;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}
.unsaved-modal-body {
  font-size: 15px;
  color: #444;
  margin-bottom: 18px;
}
.unsaved-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.custom-unsaved-modal-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0;
  padding-top: 4px;
}
.custom-unsaved-modal-actions .cancel-btn {
  min-width: 90px;
  border-radius: 4px !important;
  color: black !important;
  font-size: 14px !important;
  border: 1px solid #e0e0e0 !important;
}
.custom-unsaved-modal-actions .right-actions {
  display: flex;
  gap: 8px;
}
.custom-unsaved-modal-actions .discard-btn {
  background: #f5f5f5 !important;
  color: black !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
.custom-unsaved-modal-actions .save-btn {
  background: #111 !important;
  color: #fff !important;
  border: 1px solid #111 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
</style>
